import type { ReactElement } from 'react';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import type {
  OsvComponent,
  OsvComponentSubject,
} from '@kirin/api-client/contracts/shared/osv';
import {
  osvAdvisoryUrl,
  OSV_SEVERITY_CLASSES,
  OSV_SEVERITY_LABEL,
} from '@/lib/osv';
import { cn } from '@/lib/utils';

/**
 * The vulnerable component behind an OSV finding, as its own section on the
 * alert card (KIR-4190) — the package, version, advisory and CVSS score that
 * previously existed only inside the free-text description. Uses the same row
 * shape as the sibling "DETECTED ON CLIENT" panel.
 *
 * Purely additive: KIR-4070's Advisory row keeps rendering inside
 * `AlertSourceDetails` even though the advisory id also appears here.
 *
 * Always exactly one component, never a list — a card is one `finding_key` and
 * the package identity is already part of the hashed `description`/`location`,
 * so two packages are always two alert rows.
 */

interface AlertOsvComponentSectionProps {
  osvComponent: OsvComponent;
  /** Advisory id. Optional, so the package identity still renders without it. */
  osvId?: string;
  /**
   * KIR-4481: suppress the section's own subject heading and top spacing when it
   * is embedded in the grouped-cards "Location" card. Defaults to false.
   */
  hideHeading?: boolean;
  className?: string;
}

/**
 * Findings that describe different things share one alert title, so the header
 * comes from `subject` (decided backend-side) rather than from string shapes.
 * Total `Record` on purpose: a new subject fails to compile until it has one.
 */
const HEADER_BY_SUBJECT: Record<OsvComponentSubject, string> = {
  dependency: 'CHILD DEPENDENCY',
  directDependency: 'VULNERABLE DEPENDENCY',
  extension: 'VULNERABLE COMPONENT',
};

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <div className="flex items-start gap-2 py-1">
      <div className="flex min-h-6 w-36 shrink-0 items-center text-xs text-muted-foreground">
        {label}
      </div>
      <div className="flex min-h-6 min-w-0 flex-1 items-center gap-2">
        {children}
      </div>
    </div>
  );
}

/** The advisory id as a link to its OSV.dev page, plus a copy button. */
function AdvisoryValue({ osvId }: { osvId: string }): ReactElement {
  return (
    <>
      <a
        href={osvAdvisoryUrl(osvId)}
        target="_blank"
        rel="noopener noreferrer"
        title={`View ${osvId} on OSV.dev`}
        aria-label={`View ${osvId} on OSV.dev`}
        className="inline-flex min-w-0 items-center gap-0.5 font-mono text-xs text-primary break-all hover:underline"
      >
        {osvId}
        <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
      </a>
      <CopyToClipboardButton
        text={osvId}
        variant="ghost"
        size="sm"
        compactCopiedState
        className="h-6 w-6 shrink-0 p-0"
        aria-label="Copy advisory id"
      >
        <span className="sr-only">Copy advisory id</span>
      </CopyToClipboardButton>
    </>
  );
}

export function AlertOsvComponentSection({
  osvComponent,
  osvId,
  hideHeading = false,
  className,
}: AlertOsvComponentSectionProps): ReactElement {
  const {
    subject,
    name,
    version,
    ecosystem,
    cveId,
    cvssScore,
    cvssLevel,
    fixedVersion,
    summary,
    details,
  } = osvComponent;

  // Many advisories are keyed by the CVE itself; showing the same string as
  // both Advisory and CVE reads as a bug.
  const showCve = cveId != null && cveId !== osvId;

  return (
    <div className={cn(hideHeading ? '' : 'mt-3 pt-3', className)}>
      {!hideHeading && (
        <div className="mb-2">
          <span className="text-xs font-semibold tracking-wide text-muted-foreground">
            {HEADER_BY_SUBJECT[subject]}
          </span>
        </div>
      )}

      <div className="min-w-0">
        <Row label="Name">
          <span className="font-mono text-xs break-all">{name}</span>
        </Row>
        {ecosystem && (
          <Row label="Ecosystem">
            <span className="font-mono text-xs break-all">{ecosystem}</span>
          </Row>
        )}
        <Row label="Version">
          <span className="font-mono text-xs break-all">{version}</span>
        </Row>
        {/* Omitted when the advisory publishes no fix — a row reading "None"
            would be read as "we failed to look it up". */}
        {fixedVersion && (
          <Row label="Fixed in">
            <span className="font-mono text-xs break-all">{fixedVersion}</span>
          </Row>
        )}
        {osvId && (
          <Row label="Advisory">
            <AdvisoryValue osvId={osvId} />
          </Row>
        )}
        {showCve && (
          <Row label="CVE">
            <a
              href={osvAdvisoryUrl(cveId)}
              target="_blank"
              rel="noopener noreferrer"
              title={`View ${cveId} on OSV.dev`}
              className="inline-flex min-w-0 items-center gap-0.5 font-mono text-xs text-primary break-all hover:underline"
            >
              {cveId}
              <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
            </a>
          </Row>
        )}
        {/* Score and band are shown as two separate fields. Each is omitted
            independently when absent (common for `MAL-` records with no CVSS
            vector); "Unknown" would look like a data failure. */}
        {cvssScore != null && (
          <Row label="CVSS Score">
            <span className="font-mono text-xs break-all">
              {cvssScore.toFixed(1)}
            </span>
          </Row>
        )}
        {cvssLevel != null && (
          <Row label="Advisory Severity">
            <Badge
              variant="outline"
              className={OSV_SEVERITY_CLASSES[cvssLevel]}
            >
              {OSV_SEVERITY_LABEL[cvssLevel]}
            </Badge>
          </Row>
        )}
        {/* The advisory's own prose, last so it never pushes the identity rows
            below the fold. Rendered as text: OSV publishes markdown, and this
            card has no renderer for it. */}
        {summary && (
          <Row label="Summary">
            <span className="text-xs">{summary}</span>
          </Row>
        )}
        {details && (
          <Row label="Description">
            <span className="max-h-40 overflow-y-auto text-xs whitespace-pre-wrap">
              {details}
            </span>
          </Row>
        )}
      </div>
    </div>
  );
}
